import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import './more-participants.scss';
import '../global-left-menu/more-groups-or-entities/more-groups-or-entities.scss';
import DialogPanel from '../dialog-panel/dialog-panel';
import DialogPanelBody from '../dialog-panel/dialog-panel-body/dialog-panel-body';
import UserImage from '../user-image/user-image';
import { CloseIcon, IncidentsNoData, SearchIcon } from '../icons/icons';
import getInitials from '../../helpers/utils';
import { FETCH_PARTICIPANTS } from '../../graphQL/orderList';
import NoData from '../no-data/no-data';
import GlobalLoader from '../global-loader/global-loader';
import { FETCH_LOCATIONS_COLLABS } from '../../graphQL/supplierQuery';
import { FETCH_PARTICIPANTS_PRICING } from '../../graphQL/pricingList';
import StaticListingStatus from '../listing-status/static-listing-status';
import Action from '../action/action';

// This is clone component of MoreGroupsOrEntities, refer it in case of any updates
function MoreParticipants({ details }) {
  const [t] = useTranslation('globalleftmenu');
  const [t2] = useTranslation('manageorders');

  const [query, setQuery] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [listData, setListData] = useState(
    details.module === 'materialMapping' ? details.data : []
  );
  // Store the original full list of participants to revert to after clearing search
  const [originalListData, setOriginalListData] = useState([]);
  const [loading, setLoading] = useState(false);
  const popupRef = useRef(null);
  const { loading: participantsLoading } = useQuery(
    details?.module === 'pricing'
      ? FETCH_PARTICIPANTS_PRICING
      : FETCH_PARTICIPANTS,
    {
      variables: {
        input: {
          moduleId: details?.id,
          moduleType: details?.module,
          ...(details?.column && { column: details?.column }),
        },
      },
      skip: details?.calledAt !== 'PO' || details.module === 'materialMapping',
      onCompleted: (data) => {
        const fetchedData =
          details?.module === 'pricing'
            ? data?.listUserCollab?.data
            : data?.FetchUserCollab?.data;
        setListData(fetchedData);
        setOriginalListData(fetchedData);
      },
    }
  );

  const { loading: locationsCollabLoading } = useQuery(
    FETCH_LOCATIONS_COLLABS,
    {
      variables: {
        input: {
          entityId: details?.id,
        },
      },
      skip:
        details?.calledAt !== 'locations' ||
        details.module === 'materialMapping',
      onCompleted: (data) => {
        const fetchedData = data?.GetUsers?.data;
        setListData(fetchedData);
        setOriginalListData(fetchedData);
      },
    }
  );

  useEffect(() => {
    if (details?.calledAt === 'PO') {
      setLoading(participantsLoading);
    } else if (details?.calledAt === 'locations') {
      setLoading(locationsCollabLoading);
    }
    if (details.module === 'materialMapping' && details.data.length > 0) {
      setOriginalListData(details.data);
    }
  }, [
    locationsCollabLoading,
    participantsLoading,
    details.module,
    details.data,
  ]);

  const handleChange = (e) => {
    setQuery(e?.target?.value?.toLowerCase());
    setSearchValue(e?.target?.value);
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setQuery('');
    setListData(originalListData);
  };

  const filteredList = React.useMemo(() => {
    if (!listData) return [];
    return listData.filter(
      (val) =>
        `${val?.firstName} ${val?.lastName}`?.toLowerCase()?.includes(query) ||
        val?.email?.toLowerCase()?.includes(query)
    );
  }, [listData, query]);

  useEffect(() => {
    popupRef.current.focus();
  }, []);

  return (
    <div className="more-participants-container">
      <DialogPanel
        title={
          details?.column === 'owners'
            ? t2('manageordersscreen.ordercardrow.owners')
            : t2('manageordersscreen.participantpopup.title')
        }
      >
        <DialogPanelBody>
          {loading && (
            <GlobalLoader newLoad={true} height="6.944vw" width="6.944vw" />
          )}
          {!loading && (
            <>
              <div className="more-participants-search">
                <input
                  type="text"
                  placeholder={t2('manageordersscreen.participantpopup.search')}
                  onChange={handleChange}
                  value={searchValue}
                  ref={popupRef}
                />
                {searchValue ? (
                  <button
                    type="button"
                    className="clear-search"
                    onClick={handleClearSearch}
                  >
                    <span>
                      <CloseIcon size="1.111vw" color="#C2C0C0" />
                    </span>
                  </button>
                ) : (
                  <SearchIcon height="1.111vw" width="1.111vw" />
                )}
              </div>
              {listData?.length ? (
                <div
                  className={
                    filteredList.length > 0
                      ? 'more-participants-list'
                      : 'more-participants-list-no-scroll'
                  }
                >
                  {listData
                    ?.filter(
                      (val) =>
                        `${val?.firstName} ${val?.lastName}`
                          ?.toLowerCase()
                          ?.includes(query) ||
                        val?.email?.toLowerCase()?.includes(query)
                    )
                    ?.map((val) => (
                      <div
                        className="more-participants justify-between"
                        key={val?.userSub}
                      >
                        <div className="d-flex w-100-150 align-center">
                          <UserImage
                            removeTooltip
                            path={val?.profilePicture}
                            shortName={getInitials(
                              `${val?.firstName} ${val?.lastName}`
                            )}
                            size="small"
                            extraInfo={val.owner}
                            extraInfoTooltip={t2(
                              'manageordersscreen.ordercardrow.owner'
                            )}
                          />
                          <div className="more-participants-detail">
                            <span className="more-participants-name">
                              {val?.firstName} {val?.lastName}
                            </span>
                            <span className="more-participants-email">
                              {val?.email}
                            </span>
                          </div>
                        </div>
                        <div className="d-flex align-center">
                          {val?.role?.slice(0, 1).map((value) => {
                            return (
                              <StaticListingStatus
                                statusLabel={value}
                                className="mx-w-300"
                                textClass="mx-w-100"
                                tooltipClass="d-inline-flex"
                              />
                            );
                          })}
                          {val?.role?.length > 1 && (
                            <Action
                              label={val.role.length - 1}
                              className="count-button primary"
                            >
                              {val?.role?.slice(1).map((role) => (
                                <button type="button">{role}</button>
                              ))}
                            </Action>
                          )}
                        </div>
                      </div>
                    ))}
                  {listData?.filter(
                    (val) =>
                      `${val?.firstName} ${val?.lastName}`
                        .toLowerCase()
                        .includes(query) ||
                      val?.email?.toLowerCase()?.includes(query)
                  )?.length === 0 && (
                    <label className="no-search-data">
                      <span>{t('searchpopup.noresult')}</span>
                    </label>
                  )}
                </div>
              ) : (
                <NoData image={<IncidentsNoData size="250px" />} />
              )}
            </>
          )}
        </DialogPanelBody>
      </DialogPanel>
    </div>
  );
}

MoreParticipants.propTypes = {
  details: PropTypes.instanceOf(Object),
};

MoreParticipants.defaultProps = {
  details: {},
};

export default MoreParticipants;
